import { IComponentOptions, IPromise, IIntervalService, IOnChangesObject, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { WhiteboardsService } from "../whiteboards-service";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IAccess, IAllowedActionsMap } from "@gtmhub/sessions";
import { IWhiteboard, ICollaborator } from "../models";
import { PublishDraftsModal } from "./publish-drafts.component";
import { WhiteboardStateHolder } from "../rendering/state/whiteboard-state-holder";
import { GtmhubController } from "@gtmhub/core";
import { whiteboardsState, whiteboardState } from "../whiteboards.states";
import { WhiteboardsUserSettingsService } from "../whiteboards-user-settings-service";
import { WhiteboardEvents } from "../events";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions, IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { IdMap } from "@gtmhub/util";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { PublishDraftsV2Modal } from "./publish-drafts-v2.component";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";

export const AUTO_SAVE_INTERVAL = 5000;

const bindStateToThis = (state: IAssigneesStoreState) => {
  return {
    assigneesIdMap: state.assignees.map,
  };
};

export class WhiteboardHeaderComponentCtrl extends GtmhubController implements ReturnType<typeof bindStateToThis> {
  // bindings
  whiteboard: IWhiteboard;
  loading: IIndicator;
  currentUserAllowedActions: (keyof IAllowedActionsMap)[];
  takeCanvasSnapshot: () => string;
  showConnectionTroubleErrorMsg: boolean;

  currentUserAllowedActionsMap: IAllowedActionsMap;
  saving: IIndicator;
  collaborators: IdMap<ICollaborator>;
  subtitle = { text: localize("whiteboards"), action: { state: "gtmhub.whiteboards" } };
  sessionManagementEnabled: boolean;
  assigneesIdMap: IAssigneeIdMap;

  private autoSavePromise: IPromise<void>;
  private events: WhiteboardEvents;
  private restrictedAccessModal: IModalServiceInstance;
  private whiteboardNotAvailableModal: IModalServiceInstance;
  private confirmDeleteModal: IModalServiceInstance;
  private favoritesManager: FavoritesManager;

  static $inject = [
    "$interval",
    "$state",
    "$scope",
    "WhiteboardsService",
    "WhiteboardStateHolder",
    "PublishDraftsModal",
    "PublishDraftsV2Modal",
    "UIErrorHandlingService",
    "WhiteboardsUserSettingsService",
    "$ngRedux",
    "AssigneeActions",
    "$rootScope",
    "$uibModal",
    "FavoritesManagerFactory",
    "FeatureToggleService",
  ];

  constructor(
    private $interval: IIntervalService,
    private $state: StateService,
    $scope: IScope,
    private whiteboardsService: WhiteboardsService,
    private whiteboardStateHolder: WhiteboardStateHolder,
    private publishDraftsModal: PublishDraftsModal,
    private publishDraftsV2Modal: PublishDraftsV2Modal,
    private uiErrorHandlingService: UIErrorHandlingService,
    private whiteboardsUserSettingsService: WhiteboardsUserSettingsService,
    $ngRedux: INgRedux,
    assigneeActions: AssigneeActions,
    private $rootScope: ITraceRootScopeService & IGtmhubRootScopeService,
    private $uibModal: IModalService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private featureToggleService: FeatureToggleService
  ) {
    super();
    if ($rootScope.newExperienceEnabledForUser) {
      this.favoritesManager = this.favoritesManagerFactory(["whiteboard"]);
      this.favoritesManager.initFavorites();
    }

    this.featureToggleService.isFeatureAvailable(FeatureFlag.WhiteboardsSessionManagement).then((enabled) => (this.sessionManagementEnabled = enabled));

    this.onDestroy($ngRedux.connect(bindStateToThis)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());

    this.events = new WhiteboardEvents($scope);
    this.events.onSocketError(() => {
      this.showConnectionTroubleErrorMsg = true;
    });
    this.events.onSocketOpened(() => {
      this.showConnectionTroubleErrorMsg = false;
    });
  }

  $onChanges(changesObj: IOnChangesObject): void {
    // if the loading indicator is undefined, it means all the requests in the whiteboard component are completed successfully therefore the values are binded
    if (changesObj.loading && !changesObj.loading.currentValue) {
      this.currentUserAllowedActionsMap = this.currentUserAllowedActions.reduce((acc, action) => ((acc[action] = true), acc), {});

      if (this.currentUserAllowedActionsMap.update) {
        this.whiteboardStateHolder.loaded.then(() => {
          this.triggerAutoSave();
          this.onDestroy(() => this.cancelAutoSave());
        });
      } else {
        this.cancelAutoSave();
      }
    }
  }

  nameUpdated(): void {
    this.$rootScope.traceAction("update_whiteboard_name", () => {
      if (!this.currentUserAllowedActionsMap.update) {
        return;
      }

      this.whiteboardsService.update(this.whiteboard.id, { name: this.whiteboard.name }).catch((rejection) => {
        this.uiErrorHandlingService.handleModal(rejection);
      });
    });
  }

  publish(): void {
    if (!this.currentUserAllowedActionsMap.update) {
      return;
    }

    if (this.sessionManagementEnabled) {
      this.publishDraftsV2Modal.open(this.whiteboard.id);
      return;
    }

    this.publishDraftsModal.open(this.whiteboard.id);
  }

  updateAccess(access: IAccess): void {
    if (!this.currentUserAllowedActionsMap.modifyPermissions) {
      return;
    }

    this.$rootScope.traceAction("update_access", () => {
      this.whiteboardsService.update(this.whiteboard.id, { access }).then(
        () => {
          this.whiteboard.access = access;
        },
        (rejection) => {
          this.uiErrorHandlingService.handleModal(rejection);
        }
      );
    });
  }

  delete(): void {
    if (!this.currentUserAllowedActionsMap.delete) {
      return;
    }
    this.showConfirmDeleteModal();
  }

  clone(): void {
    this.$rootScope.traceAction("clone_whiteboard", () => {
      this.whiteboardsService.clone(this.whiteboard.id).then(
        (whiteboard) => this.$state.go(whiteboardState.name, { whiteboardId: whiteboard.id }),
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  }

  triggerAutoSave(): void {
    if (!this.currentUserAllowedActionsMap.update) {
      return;
    }

    delete this.saving;
    this.cancelAutoSave();

    this.autoSavePromise = this.$interval(() => {
      this.$rootScope.traceAction("auto_saving", () => {
        if (!this.whiteboardStateHolder.dirty) {
          return;
        }

        this.saving = { progress: true };
        const canvasDataUrl = this.takeCanvasSnapshot();
        this.whiteboardStateHolder.updatePicture(canvasDataUrl).then(
          () => delete this.saving,
          (error) => this.handleError(error)
        );
      });
    }, AUTO_SAVE_INTERVAL);
  }

  isFavorite(): boolean {
    return this.favoritesManager?.entityIsFavorite({ id: this.whiteboard.id });
  }

  addToFavorites(): void {
    this.favoritesManager?.addToFavorites(this.whiteboard as { id: string }, "whiteboard", this.getNavItem());
  }

  removeFromFavorites(): void {
    this.favoritesManager?.removeFromFavorites(this.whiteboard as { id: string }, this.getNavItem());
  }

  private getNavItem(): INavItem {
    return this.favoritesManager?.getFavoriteItem({ id: this.whiteboard.id }) || navItemsFactory.buildWhiteboardNavItem(this.whiteboard);
  }

  private handleError(error): void {
    switch (error.status) {
      case 404:
        delete this.saving;
        this.$state.go(whiteboardsState.name).then(() => this.showWhiteboardNotFoundModal());
        break;
      case 403:
        delete this.saving;
        this.events.broadcastLockWhiteboard();
        this.showRestrictedAccessModal();
        break;
      default:
        this.saving = { error };
    }
    this.cancelAutoSave();
  }

  private showRestrictedAccessModal = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.restrictedAccessModal = this.$uibModal.open({
      templateUrl: require("./restricted-access.jade"),
      controller: [
        "$scope",
        function ($scope) {
          $scope.close = () => {
            ctrl.restrictedAccessModal.close();
          };
        },
      ],
    });
  };

  private showConfirmDeleteModal = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.confirmDeleteModal = this.$uibModal.open({
      templateUrl: require("./confirm-delete.jade"),
      controller: [
        "$scope",
        function ($scope) {
          $scope.close = () => {
            ctrl.confirmDeleteModal.close();
          };
          $scope.delete = () => {
            ctrl.$rootScope.traceAction("delete_whiteboard", () => {
              ctrl.whiteboardsService.delete(ctrl.whiteboard).then(
                () => {
                  ctrl.whiteboardsUserSettingsService.remove(ctrl.whiteboard.id);
                  ctrl.confirmDeleteModal.close();
                  ctrl.$state.go(whiteboardsState.name);
                },
                (error) => ctrl.uiErrorHandlingService.handleModal(error)
              );
            });
          };
        },
      ],
    });
  };

  private showWhiteboardNotFoundModal = () => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.whiteboardNotAvailableModal = this.$uibModal.open({
      templateUrl: require("./whiteboard-not-available.jade"),
      controller: [
        "$scope",
        function ($scope) {
          $scope.close = () => {
            ctrl.whiteboardNotAvailableModal.close();
          };
        },
      ],
    });
  };

  private cancelAutoSave(): void {
    if (this.autoSavePromise) {
      this.$interval.cancel(this.autoSavePromise);
      delete this.autoSavePromise;
    }
  }
}

export const WhiteboardHeaderComponent: IComponentOptions = {
  controller: WhiteboardHeaderComponentCtrl,
  templateUrl: require("./whiteboard-header.jade"),
  bindings: {
    loading: "<",
    whiteboard: "<",
    currentUserAllowedActions: "<",
    takeCanvasSnapshot: "<",
    collaborators: "<",
  },
};
